import React from 'react';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useOnlineStatus } from '../../../common/hooks/useOnlineStatus';
import CustomMessage from '../../../common/components/Messages/CustomMessage';

function OrderFlowModal({
  open,
  onClose,
  titleOpening,
  orderCode,
  storeName,
  children,
  primaryLabel,
  secondaryLabel,
  onPrimary,
  onSecondary,
  primaryDisabled,
  isSubmitting,
  showSubmitError,
}) {
  const { t } = useTranslation(['glossary', 'system']);
  const { isOnline } = useOnlineStatus();

  const primaryHandler = (e) => {
    e.preventDefault();
    if (primaryDisabled || !isOnline || isSubmitting) return;
    onPrimary?.();
  };

  const secondaryHandler = (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    onSecondary?.();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      aria-labelledby="order-flow-modal-title"
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
        },
      }}
      PaperProps={{
        elevation: 6,
        sx: {
          borderRadius: 3,
          m: { xs: 2, sm: 3 },
          maxHeight: 'calc(100vh - 32px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          fontFamily: (theme) => theme.typography.fontFamily,
        },
      }}
    >
      <DialogContent
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          px: { xs: 3, sm: 4 },
          py: { xs: 3, sm: 4 },
          pb: { xs: 3, sm: 4 },
          fontFamily: (theme) => theme.typography.fontFamily,
        }}
      >
        <Typography
          id="order-flow-modal-title"
          component="div"
          role="heading"
          aria-level={2}
          sx={{
            mb: 3,
            lineHeight: 1.4,
            fontWeight: 500,
            fontFamily: (theme) => theme.typography.fontFamily,
            color: 'text.primary',
            fontSize: { xs: '1.0625rem', sm: '1.1875rem' },
            letterSpacing: '-0.01em',
          }}
        >
          <Box component="span">¿{titleOpening} </Box>
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
            }}
          >
            {orderCode}
          </Box>
          <Box component="span"> {t('of_store', { ns: 'glossary' })} </Box>
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
            }}
          >
            {storeName}
          </Box>
          <Box component="span">?</Box>
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            width: '100%',
          }}
        >
          {children}
        </Box>

        <Box
          component="footer"
          sx={{
            mt: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            width: '100%',
          }}
        >
          {!isOnline && (
            <CustomMessage
              message={t('OFFLINE', { ns: 'system' })}
              variant="error"
            />
          )}

          <Button
            type="button"
            variant="contained"
            disableElevation
            fullWidth
            disabled={primaryDisabled || !isOnline || isSubmitting}
            onClick={primaryHandler}
            sx={{
              py: 1.35,
              fontFamily: (theme) => theme.typography.fontFamily,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.8125rem',
              lineHeight: 1.25,
              borderRadius: '6px',
              bgcolor: '#6236C1',
              color: '#fff',
              boxShadow: '0 2px 6px rgba(98, 54, 193, 0.35)',
              '&:hover': {
                bgcolor: '#5529b0',
                boxShadow: '0 3px 8px rgba(98, 54, 193, 0.42)',
              },
              '&:disabled': {
                bgcolor: 'rgba(98, 54, 193, 0.42)',
                color: 'rgba(255, 255, 255, 0.85)',
                boxShadow: 'none',
              },
            }}
          >
            {primaryLabel}
          </Button>

          <Button
            type="button"
            variant="contained"
            disableElevation
            fullWidth
            disabled={isSubmitting}
            onClick={secondaryHandler}
            sx={{
              py: 1.35,
              fontFamily: (theme) => theme.typography.fontFamily,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.8125rem',
              lineHeight: 1.25,
              borderRadius: '6px',
              bgcolor: '#E0E0E0',
              color: 'rgba(0, 0, 0, 0.87)',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12)',
              '&:hover': {
                bgcolor: '#D5D5D5',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.16)',
              },
              '&:disabled': {
                bgcolor: '#E0E0E0',
                color: 'rgba(0, 0, 0, 0.35)',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            {secondaryLabel}
          </Button>

          {showSubmitError && (
            <Typography
              variant="body2"
              color="error"
              align="center"
              sx={{ fontFamily: (theme) => theme.typography.fontFamily }}
            >
              {t('GENERAL_ERROR', { ns: 'system' })}
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default OrderFlowModal;
